import {
  DocsCallout,
  DocsContent,
  DocsLayout,
  DocsScreenshot,
  DocsStepList,
  H2,
  H3,
} from '@/components/docs'

export default function DocsGettingStarted() {
  return (
    <DocsLayout>
      <DocsContent
        title="Quick Start"
        description="Get up and running with XENDREL in minutes. This guide walks you through creating your first case, uploading a document, and exploring the resulting knowledge graph."
        previousPage={{ title: 'Overview', href: '/docs' }}
        nextPage={{ title: 'Case Management', href: '/docs/case-management' }}
      >
        <DocsCallout variant="info" title="Prerequisites">
          You need a XENDREL account to follow this guide. If you don't have one yet, contact your
          administrator or sign up at the login page.
        </DocsCallout>

        <H2 id="creating-your-first-case">Creating Your First Case</H2>

        <DocsStepList
          steps={[
            {
              title: 'Navigate to Cases',
              description: (
                <>
                  After logging in, you'll land on the Cases page. This is your home base where all
                  your investigations are organized.
                </>
              ),
            },
            {
              title: 'Click "New Case"',
              description: (
                <>
                  Click the <strong className="text-foreground">New Case</strong> button in the top
                  right corner of the Cases page.
                </>
              ),
            },
            {
              title: 'Enter Case Details',
              description: (
                <>
                  Give your case a descriptive name (e.g., "Financial Investigation Q1 2024"). You
                  can also add a description with notes about the investigation scope and
                  objectives.
                </>
              ),
            },
            {
              title: 'Create the Case',
              description: (
                <>
                  Click <strong className="text-foreground">Create</strong>. You'll be taken to the
                  case detail page where you can start adding documents.
                </>
              ),
            },
          ]}
        />

        <DocsScreenshot
          src="/docs/images/getting-started/dashboard-overview.svg"
          alt="XENDREL Cases Dashboard"
          caption="The Cases dashboard showing your investigations"
        />

        <H2 id="uploading-your-first-document">Uploading Your First Document</H2>

        <DocsStepList
          steps={[
            {
              title: 'Open the Documents Panel',
              description: (
                <>
                  From your case detail page, click the{' '}
                  <strong className="text-foreground">Documents</strong> button in the toolbar. This
                  opens the document management panel.
                </>
              ),
            },
            {
              title: 'Start the Upload',
              description: (
                <>
                  Click <strong className="text-foreground">Upload Document</strong> or drag and
                  drop your file directly onto the upload area. XENDREL accepts PDF, DOCX, CSV,
                  XLSX, and image files for ID documents.
                </>
              ),
            },
            {
              title: 'Select Processing Pipeline',
              description: (
                <>
                  Choose the appropriate pipeline for your document:
                  <ul className="mt-2 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 dark:text-amber-500">•</span>
                      <span>
                        <strong className="text-foreground">Standard</strong> - General documents
                        like reports and correspondence
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 dark:text-amber-500">•</span>
                      <span>
                        <strong className="text-foreground">Financial</strong> - Bank statements and
                        financial records
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 dark:text-amber-500">•</span>
                      <span>
                        <strong className="text-foreground">Identity</strong> - Passports, IDs, and
                        travel documents
                      </span>
                    </li>
                  </ul>
                </>
              ),
            },
            {
              title: 'Wait for Processing',
              description: (
                <>
                  The document will be processed by our AI extraction pipeline. You can see the
                  progress in the document list. Processing typically takes 30 seconds to a few
                  minutes depending on document length.
                </>
              ),
            },
          ]}
        />

        <DocsCallout variant="tip" title="Pro Tip">
          You can upload multiple documents at once by selecting them all in the file picker or
          dragging a batch of files onto the upload area.
        </DocsCallout>

        <H2 id="exploring-the-knowledge-graph">Exploring the Knowledge Graph</H2>

        <p className="text-foreground mb-6 leading-relaxed">
          Once your document is processed, XENDREL automatically builds a knowledge graph from the
          extracted entities and their relationships.
        </p>

        <H3 id="graph-view">Graph View</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          The main graph view shows entities as nodes and relationships as connecting lines. Nodes
          are color-coded by type:
        </p>

        <ul className="space-y-2 my-4">
          <li className="flex items-center gap-3 text-foreground">
            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
            <span>
              <strong className="text-foreground">Blue</strong> - People
            </span>
          </li>
          <li className="flex items-center gap-3 text-foreground">
            <span className="w-3 h-3 rounded-full bg-purple-500"></span>
            <span>
              <strong className="text-foreground">Purple</strong> - Organizations
            </span>
          </li>
          <li className="flex items-center gap-3 text-foreground">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span>
              <strong className="text-foreground">Green</strong> - Locations
            </span>
          </li>
          <li className="flex items-center gap-3 text-foreground">
            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
            <span>
              <strong className="text-foreground">Amber</strong> - Documents
            </span>
          </li>
        </ul>

        <DocsScreenshot
          src="/docs/images/graph/graph-explorer.svg"
          alt="XENDREL Knowledge Graph"
          caption="The knowledge graph showing entities and their connections"
        />

        <H3 id="interacting-with-nodes">Interacting with Nodes</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Click on any node to see its details in the side panel. You can:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>View all properties extracted from documents</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>See which documents mention this entity</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Expand to show connected entities</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Find paths to other entities</span>
          </li>
        </ul>

        <H2 id="running-your-first-query">Running Your First Query</H2>

        <DocsStepList
          steps={[
            {
              title: 'Open the Chat Panel',
              description: (
                <>
                  Click the <strong className="text-foreground">Chat</strong> button in the toolbar
                  to open the natural language query interface.
                </>
              ),
            },
            {
              title: 'Ask a Question',
              description: (
                <>
                  Type a question in plain English, such as:
                  <ul className="mt-2 space-y-1">
                    <li className="text-muted-foreground">"Show me all people in this case"</li>
                    <li className="text-muted-foreground">
                      "What organizations is John Smith connected to?"
                    </li>
                    <li className="text-muted-foreground">"Find all transactions over $50,000"</li>
                  </ul>
                </>
              ),
            },
            {
              title: 'View Results',
              description: (
                <>
                  XENDREL translates your question into a graph query and returns matching entities.
                  Click on any result to highlight it in the graph.
                </>
              ),
            },
          ]}
        />

        <DocsCallout variant="tip" title="What's Next?">
          Now that you've created your first case and explored the basics, dive deeper into:
          <ul className="mt-2 space-y-1">
            <li className="flex items-start gap-2">
              <span className="text-green-600 dark:text-green-400">•</span>
              <span>
                <a
                  href="/docs/case-management"
                  className="text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 underline"
                >
                  Case Management
                </a>{' '}
                - Learn about case workflows and organization
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 dark:text-green-400">•</span>
              <span>
                <a
                  href="/docs/document-processing"
                  className="text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 underline"
                >
                  Document Processing
                </a>{' '}
                - Understanding extraction pipelines
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600 dark:text-green-400">•</span>
              <span>
                <a
                  href="/docs/graph-explorer"
                  className="text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 underline"
                >
                  Graph Explorer
                </a>{' '}
                - Advanced graph navigation techniques
              </span>
            </li>
          </ul>
        </DocsCallout>
      </DocsContent>
    </DocsLayout>
  )
}
